// Разбивката на продаденото ПРИ ОТЧЕТА — кой сервитьор и коя станция какво е
// продал. Само информация (само бройки, без пари): не влияе на изписването и
// на записа. Два блока, свити по подразбиране (изборът се помни в браузъра):
//  • „Сервитьори" — по ред на запис „Мария · 8 сметки · 39 бр"; клик → рецептите
//    му × к-во;
//  • „Станции" — „Бар · → склад Бар · 51 бр" (или „без склад"); клик → рецептите
//    × к-во.
// Два вида:
//  • `card` — под избраната карта в лявата колона с чакащите (264px, десктоп);
//  • `inline` — на таблет/телефон (там списъкът е лента над детайла): сгъваем
//    ред „Сервитьори и станции ▸" под заглавието на отчета, свит по подразбиране.
// Данните — същите заявки като в детайла (общ кеш на React Query): разбивката
// (`useReportSalesBreakdown`) и имената на рецептите от прегледа. Зарежда се
// паралелно и НЕ блокира „Разнеси"; докато не е дошло или при грешка (стар BE
// без пътя — 422) блокът просто липсва. Логиката е в
// `reportPostingSalesBreakdownModel.ts`.
import { useId, useMemo, useState } from "react";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useReportPostingPreview } from "../../../../hooks/useInventory";
import { useReportSalesBreakdown } from "../../../../hooks/useInventorySoldAnalysis";
import { ROW_HEADER, ROW_HEADER_TEXT } from "../../components/list/density";
import { TruncatedText } from "../../components/list/TruncatedText";
import { formatCount } from "./reportPostingShared";
import { POSTING_READ_ROW, POSTING_TAP_ROW } from "./postingDensity";
import {
  groupSoldByStation,
  groupSoldByWaiter,
  hasSalesBreakdown,
  type SoldGroup,
} from "./reportPostingSalesBreakdownModel";

type SectionKind = "waiters" | "stations";

// Кои блокове са разгънати — в localStorage, за да остане същото при следващия
// отчет; без хранилище блокът работи, само не помни
const OPEN_SECTIONS_STORAGE_KEY = "inventory_posting_breakdown_open";

const readOpenSections = (): SectionKind[] => {
  try {
    const stored = localStorage.getItem(OPEN_SECTIONS_STORAGE_KEY) ?? "";
    return stored.split(",").filter((kind): kind is SectionKind => kind === "waiters" || kind === "stations");
  } catch {
    return [];
  }
};

const writeOpenSections = (sections: SectionKind[]) => {
  try {
    localStorage.setItem(OPEN_SECTIONS_STORAGE_KEY, sections.join(","));
  } catch {
    // пълно/забранено хранилище — изборът важи до смяна на отчета
  }
};

const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2";

const Chevron = ({ open, hidden = false }: { open: boolean; hidden?: boolean }) => (
  <RightOutlined
    aria-hidden
    className={`shrink-0 text-[9px] text-muted transition-transform duration-150 ${open ? "rotate-90" : ""} ${
      hidden ? "invisible" : ""
    }`}
  />
);

// Един сервитьор / една станция: ред „име · вторично · общо бр", при клик —
// рецептите × к-во отдолу. Без продажби (почистени сметки) — не се разгъва.
const GroupRow = ({ group, padding }: { group: SoldGroup; padding: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const empty = group.lines.length === 0;
  const secondary =
    group.billsCount !== undefined
      ? t("inventoryPage.postingPickBills", { count: group.billsCount })
      : group.warehouseName === undefined
        ? null
        : group.warehouseName
          ? t("inventoryPage.postingSoldStationWarehouse", { name: group.warehouseName })
          : t("inventoryPage.postingSoldStationNoWarehouse");
  const fullText = [group.title, secondary].filter(Boolean).join(" · ");
  return (
    <div data-breakdown-group className="border-t border-shade-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={empty}
        aria-expanded={empty ? undefined : open}
        title={fullText}
        className={`${ROW_GRID} ${POSTING_TAP_ROW} ${padding} w-full border-none bg-transparent text-left outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] ${
          empty ? "cursor-default" : "cursor-pointer hover:bg-fill"
        }`}
      >
        <span className="flex min-w-0 items-center gap-1">
          <Chevron open={open && !empty} hidden={empty} />
          {/* името не се свива под 60%, реже се вторичното */}
          <span className={`truncate font-medium text-ink ${secondary ? "max-w-[60%] shrink-0" : "min-w-0"}`}>
            {group.title}
          </span>
          {secondary && (
            <span
              className={`min-w-0 truncate text-[12px] ${
                group.warehouseName === null ? "text-brand-deep-ink" : "text-muted"
              }`}
            >
              · {secondary}
            </span>
          )}
        </span>
        <span className="whitespace-nowrap text-right font-sheet text-ink tabular-nums">
          {t("inventoryPage.postingSoldTotalQty", { qty: formatCount(group.totalQuantity) })}
        </span>
      </button>
      {open &&
        group.lines.map((line) => (
          <div
            key={line.sellableId}
            className={`${ROW_GRID} ${POSTING_READ_ROW} ${padding} border-t border-shade-1 bg-fill/60`}
          >
            <span className="flex min-w-0 items-center gap-1.5 pl-[15px]">
              <TruncatedText text={line.name} className="text-body" />
              {line.isProduct && (
                <span className="shrink-0 rounded bg-fill-2 px-1.5 text-[10.5px] font-medium leading-4 text-body">
                  {t("inventoryPage.postingProductTag")}
                </span>
              )}
            </span>
            <span className="text-right font-sheet text-ink tabular-nums">{formatCount(line.quantity)}</span>
          </div>
        ))}
    </div>
  );
};

// Блокът „Сервитьори" / „Станции": заглавен ред (свива/разгъва) + редовете
const Section = ({
  kind,
  groups,
  open,
  onToggle,
  padding,
  className = "",
}: {
  kind: SectionKind;
  groups: SoldGroup[];
  open: boolean;
  onToggle: () => void;
  padding: string;
  // разделителят към предишния блок (черта отгоре или отляво)
  className?: string;
}) => {
  const { t } = useTranslation();
  const bodyId = useId();
  const label =
    kind === "waiters" ? t("inventoryPage.postingBreakdownWaiters") : t("inventoryPage.postingBreakdownStations");
  return (
    <div data-breakdown-section={kind} className={`min-w-0 ${className}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className={`flex w-full cursor-pointer items-center gap-1.5 border-none bg-transparent text-left outline-none hover:bg-fill focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] pointer-coarse:min-h-11 ${ROW_HEADER} ${padding}`}
      >
        <Chevron open={open} />
        <span className={ROW_HEADER_TEXT}>{label}</span>
        <span className="ml-auto text-[12px] leading-[normal] text-muted tabular-nums">{groups.length}</span>
      </button>
      <div id={bodyId} className={open ? "" : "hidden"}>
        {groups.map((group) => (
          <GroupRow key={group.key} group={group} padding={padding} />
        ))}
      </div>
    </div>
  );
};

type Props = {
  reportId: number;
  variant: "card" | "inline";
};

export const PendingReportBreakdown = ({ reportId, variant }: Props) => {
  const { t } = useTranslation();
  const bodyId = useId();
  const breakdown = useReportSalesBreakdown(reportId).data ?? null;
  const recipes = useReportPostingPreview(reportId).data?.recipes;
  const [openSections, setOpenSections] = useState(readOpenSections);
  const [inlineOpen, setInlineOpen] = useState(false);

  const waiterGroups = useMemo(
    () => (breakdown && recipes ? groupSoldByWaiter(breakdown, recipes) : []),
    [breakdown, recipes],
  );
  const stationGroups = useMemo(
    () => (breakdown && recipes ? groupSoldByStation(breakdown, recipes) : []),
    [breakdown, recipes],
  );
  // Без разбивка (зарежда се, грешка, стар BE) или без имената от прегледа —
  // блокът липсва, нищо друго не чака
  if (!recipes || !hasSalesBreakdown(breakdown)) return null;

  const toggleSection = (kind: SectionKind) => {
    const next = openSections.includes(kind)
      ? openSections.filter((open) => open !== kind)
      : [...openSections, kind];
    writeOpenSections(next);
    setOpenSections(next);
  };
  const padding = variant === "card" ? "px-3" : "px-3.5";
  // Втори блок: в колоната и стеснено — черта отгоре; в двете колони на
  // по-широкия детайл — черта отляво
  const secondSectionClass =
    variant === "card"
      ? "border-t border-shade-2"
      : "border-t border-shade-2 @min-[560px]:border-l @min-[560px]:border-t-0";
  const sections = (
    <>
      <Section
        kind="waiters"
        groups={waiterGroups}
        open={openSections.includes("waiters")}
        onToggle={() => toggleSection("waiters")}
        padding={padding}
      />
      {stationGroups.length > 0 && (
        <Section
          kind="stations"
          groups={stationGroups}
          open={openSections.includes("stations")}
          onToggle={() => toggleSection("stations")}
          padding={padding}
          className={secondSectionClass}
        />
      )}
    </>
  );

  // В колоната: под картата на отчета, разделено с черта
  if (variant === "card")
    return (
      <div data-report-breakdown className="border-t border-shade-2 text-ink">
        {sections}
      </div>
    );

  const summary = [
    t("inventoryPage.postingMetaWaiters", { count: waiterGroups.length }),
    stationGroups.length ? t("inventoryPage.postingBreakdownStationsCount", { count: stationGroups.length }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    // shrink-0: в скролиращата колона на детайла карта с overflow-hidden иначе
    // се свива до 2px (docs/inventory.md §Капани)
    <section
      data-report-breakdown
      className="@container min-w-0 shrink-0 overflow-hidden rounded-xl border border-shade-2 bg-panel text-ink"
    >
      <button
        type="button"
        onClick={() => setInlineOpen(!inlineOpen)}
        aria-expanded={inlineOpen}
        aria-controls={bodyId}
        className="flex min-h-[34px] w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3.5 py-1.5 text-left outline-none hover:bg-fill focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] pointer-coarse:min-h-11"
      >
        <span className="shrink-0 text-[13px] font-semibold leading-5 text-ink">
          {t("inventoryPage.postingBreakdownTitle")}
        </span>
        <span className="min-w-0 truncate text-[12.5px] leading-5 text-muted">{summary}</span>
        <span className="ml-auto flex shrink-0">
          <Chevron open={inlineOpen} />
        </span>
      </button>
      <div
        id={bodyId}
        className={`${inlineOpen ? "grid" : "hidden"} border-t border-shade-2 @min-[560px]:grid-cols-2`}
      >
        {sections}
      </div>
    </section>
  );
};

export default PendingReportBreakdown;
